import { useEffect, useState } from 'react';

import { Button, ScreenReaderOnly, SubmitButton } from '@capsule/consumer-design-system';

import { Form, Formik } from 'formik';
import { useHistory } from 'react-router';
import { css } from 'styled-components';

import { Card, CardItem } from 'src/app/components/Elements/Card';
import { Divider } from 'src/app/components/Elements/Divider';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import { ToolTip } from 'src/app/components/Elements/ToolTip';
import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete';
import { AddressAutoComplete } from 'src/app/components/Form/ApiAutoComplete/DeliveryAddress/container';
import CheckBoxInput from 'src/app/components/Form/CheckBoxInput';
import TextInput from 'src/app/components/Form/TextInput';
import ZipCodeInput from 'src/app/components/Form/TextInput/ZipCode/container';
import { UndeliverableAddressError } from 'src/app/components/Form/TextInput/ZipCode/UndeliverableAddressError';
import type { CreateAddressRequestBody, EditAddressRequestBody } from 'src/app/hooks/data/deliveryAddress';
import {
  useDeliveryAddressCreate,
  useDeliveryAddressDelete,
  useDeliveryAddressUpdate,
} from 'src/app/hooks/data/deliveryAddress';
import { useAccountPageAddressData } from 'src/app/hooks/feature_data/useAccountPageAddressData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { accountPage, validateAddressV2 } from 'src/app/router/paths';
import type { ModalPayload } from 'src/app/state/actions';
import { closeModal, openModal } from 'src/app/state/actions';
import { openChat } from 'src/app/state/slices/chat';
import { useDispatch } from 'src/app/state/store';
import type { DeliveryAddress } from 'src/app/types/delivery';
import { theme } from 'src/styles/theme';

import { ErrorBanner, OpenChat, SubTitle } from './styles';
import styles from './styles.module.scss';
import validation from './validation';

const DEFAULT_INITIAL_ADDRESS = {
  id: undefined,
  address: '',
  zipCode: '',
  isDoorman: false,
};

const FORM_ID = 'deliveryAddressForm';
export const AccountPageAddAddress = () => {
  const history = useHistory();
  const [hasError, setHasError] = useState(false);
  // Events
  const dispatchAddDeliveryAddressEvent = useSegmentEvent('addDeliveryAddress', { shouldTrackOnMount: false });
  const dispatchEditAddressFailedEvent = useSegmentEvent('editAddressFailed', {
    shouldTrackOnMount: false,
  });
  const dispatchEditAddressSuccessfulEvent = useSegmentEvent('editAddressSuccessful', {
    shouldTrackOnMount: false,
  });
  const dispatchDeleteDeliveryAddressEvent = useSegmentEvent('deleteDeliveryAddress', {
    shouldTrackOnMount: false,
  });
  const dispatchViewAddAddressEvent = useSegmentEvent('viewAddAddress', {
    shouldTrackOnMount: false,
  });
  const dispatchViewEditExistingAddressEvent = useSegmentEvent('viewEditExistingAddress', {
    shouldTrackOnMount: false,
  });

  // Data and state hooks
  const { addressData, setAddressData } = useAccountPageAddressData();

  const initialAddress = addressData?.existingAddress || DEFAULT_INITIAL_ADDRESS;

  const isCreating = !initialAddress?.id;
  const { mutateAsync: deleteDeliveryAddressAsync } = useDeliveryAddressDelete();
  const { mutateAsync: updateDeliveryAddressAsync } = useDeliveryAddressUpdate();
  const { mutateAsync: createDeliveryAddressAsync } = useDeliveryAddressCreate();

  // We still need this redux stuff for the modal
  const dispatch = useDispatch();
  const dispatchOpenModal = ({ modalType, handleConfirmClick, confirmMessage }: ModalPayload) =>
    dispatch(openModal({ modalType, handleConfirmClick, confirmMessage }));
  const dispatchCloseModal = () => dispatch(closeModal());

  // Handlers
  const handleDeleteAddress = async () => {
    /*
     * Can only delete if we have an address already. This is here mostly
     * for the sake of the type checker. This action is triggered by a button
     * that is only present on the page if we are editing an existing address.
     */
    if (initialAddress?.id) {
      dispatchCloseModal();
      await deleteDeliveryAddressAsync(parseInt(initialAddress.id));
      dispatchDeleteDeliveryAddressEvent({ location: 'Address Form Your Account' });
      await setAddressData({});
      history.push(accountPage.toPath());
    }
  };

  const handleDelete = async () => {
    dispatchOpenModal({
      modalType: 'confirm',
      handleConfirmClick: handleDeleteAddress,
      confirmMessage: 'Are you sure you want to delete this address?',
    });
  };

  const handleUpdateAddress = async (payload: DeliveryAddress) => {
    try {
      await updateDeliveryAddressAsync(payload as EditAddressRequestBody);
      dispatchEditAddressSuccessfulEvent({
        source: 'Your Account',
      });
    } catch (error) {
      setHasError(true);
      dispatchEditAddressFailedEvent({
        source: 'Your Account',
      });
      throw error;
    }

    dispatchOpenModal({
      modalType: 'ok',
      confirmMessage: 'Great! Your delivery address has been uploaded.',
      handleConfirmClick: () => dispatchCloseModal(),
    });
    await setAddressData({});
    history.push(accountPage.toPath());
  };

  const handleCreateAddress = async (payload: DeliveryAddress) => {
    let result;
    try {
      result = await createDeliveryAddressAsync(payload as CreateAddressRequestBody);
    } catch (error) {
      setHasError(true);
      throw error;
    }
    if (result.status === 201) {
      setHasError(false);
      dispatchOpenModal({
        modalType: 'ok',
        confirmMessage: 'Great! Your delivery address has been uploaded.',
        handleConfirmClick: () => dispatchCloseModal(),
      });
      dispatchAddDeliveryAddressEvent({
        location: 'Your Account',
      });
      await setAddressData({});
      history.push(accountPage.toPath());
    } else if (result.status === 200) {
      setHasError(false);
      await setAddressData({
        invalidAddress: result.data.enteredAddress,
        suggestedAddress: result.data.suggestedAddress,
      });
      history.push(validateAddressV2.toPath());
    } else {
      setHasError(true);
    }
  };

  useEffect(() => {
    if (isCreating) {
      dispatchViewAddAddressEvent({ source: 'Your Account' });
    } else {
      dispatchViewEditExistingAddressEvent({ source: 'Your Account' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StandardPage title={isCreating ? 'Add a delivery address' : 'Edit delivery address'}>
      <div>
        <SubTitle>
          {isCreating ? (
            `Capsule delivers your medications to your home, work, a friend’s house — anywhere.`
          ) : (
            <>
              Updating your address won’t affect any active deliveries.{' '}
              <OpenChat onClick={() => dispatch(openChat())}>
                {/*
                 We had to make the OpenChat element an anchor tag so the text wrapped nicely with the previous
                 sentence. For accessibility, we're informing users of screen readers of the link's behavior.
                 */}
                <ScreenReaderOnly>
                  This link will not cause navigation to a different page. It will open a chat box in the current page.
                </ScreenReaderOnly>
                Chat with us to update an ongoing delivery
              </OpenChat>
            </>
          )}
        </SubTitle>

        <Formik
          onSubmit={async values => {
            if (initialAddress?.id) {
              await handleUpdateAddress(values);
            } else {
              await handleCreateAddress(values);
            }
          }}
          initialValues={initialAddress}
          validationSchema={validation.schema}
        >
          {({ isSubmitting }) => {
            return (
              <>
                <Form id={FORM_ID}>
                  <SegmentFormIncomplete />
                  <Card>
                    <CardItem>
                      <UndeliverableAddressError />

                      <AddressAutoComplete
                        textInputProps={{
                          name: 'address',
                          type: 'text',
                          label: 'Street address*',
                          'data-cy': 'street',
                          id: 'street',
                          helperText: '*Required',
                        }}
                        relatedFields={{ zipCode: 'zipCode' }}
                      />
                      <TextInput name="addressTwo" label="Apt, suite, floor, etc." data-cy="street_two" />
                      <ZipCodeInput
                        name="zipCode"
                        label="Zip code*"
                        helperText="*Required"
                        data_cy="zip_code"
                        compactStyle
                      />
                      <TextInput
                        name="instructions"
                        label="Delivery instructions"
                        data-cy="notes"
                        helperText="For example: access code, call from lobby, etc."
                      />
                      <CheckBoxInput
                        hasBlueSentenceLabel
                        name="isDoorman"
                        label="Leave with doorman"
                        data-cy="is_doorman"
                      />
                    </CardItem>
                  </Card>
                  <div className="mt-4 d-flex justify-content-center">
                    <ToolTip
                      anchorText="Someone must be available to receive every order. We don’t leave medications unattended."
                      header="Every Capsule order requires someone present."
                      body="For every order there needs to be someone – a friend, a doorman, a colleague – to receive and sign for your medication when it arrives."
                      analyticTip="Signature requirement"
                    />
                  </div>
                  {hasError && (
                    <ErrorBanner
                      titleText=""
                      subtitleText="Failed to save address. Please try again or chat with us for help."
                    >
                      <Button
                        css={css`
                          text-wrap: nowrap;
                        `}
                        variant={'dark-outline-md'}
                        onClick={() => dispatch(openChat())}
                      >
                        Chat
                      </Button>
                    </ErrorBanner>
                  )}
                  <SubmitButton
                    className={styles.submitButton}
                    variant="contained-secondary-lg"
                    isSubmitting={isSubmitting}
                    data-cy="deliveryAddressFormSubmitButton"
                    form={FORM_ID}
                    isFullWidth
                  >
                    Save
                  </SubmitButton>
                  {!isCreating && (
                    <>
                      <Divider margin={theme.dividerMargins.lg} />
                      <Button
                        className={styles.submitButton}
                        variant="outline-lg"
                        isFullWidth
                        onClick={handleDelete}
                        data-cy="checkout-delete-address"
                      >
                        Delete Address
                      </Button>
                    </>
                  )}
                </Form>
              </>
            );
          }}
        </Formik>
      </div>
    </StandardPage>
  );
};
